import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment/moment';
import ReactDatePicker from 'react-datepicker';
import { faFileExcel, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import GenericPageContainer from '../../components/agv-generic-page-container';
import CustomDatePickerDashboard from '../../components/agv-header-date-picker';
import SearchFilter from '../../components/agv-header-search';
import MainButton from '../../components/agv-button';
import useGetTourLogs from '../../api/tours/useGetTourLogs';
import useGetTourLogStats from '../../api/tours/useGetTourLogStats';
import useGetTourLogHistory from '../../api/tours/useGetTourLogHistory';
import useRetryTourLog from '../../api/tours/useRetryTourLog';
import useUpdateTourLogDatas from '../../api/tours/useUpdateTourLogDatas';
import getTourLog from '../../api/tours/getTourLog';
import useRetryTourLogs from '../../api/tours/useRetryTourLogs';
import useExportTourLogs from '../../api/tours/useExportTourLogs';
import LogsKpis from './components/LogsKpis';
import LogsTable from './components/LogsTable';
import LogDetailPanel from './components/LogDetailPanel';
import ConfirmRetryModal from './components/ConfirmRetryModal';
import LogPayloadEditorModal from './components/LogPayloadEditorModal';
import LogsIndicators from './components/LogsIndicators';

const TABS = { FOLLOW: 'FOLLOW', INDICATORS: 'INDICATORS' };

// Effet d'une relance sur la ligne (identique à l'API), en attendant son rechargement
// Une relance manuelle ne donne droit qu'à un essai (identique à l'API)
const MANUAL_RETRY_ATTEMPTS = 1;
const applyRetryReset = (log) => ({
  ...log,
  status: 'CREATED',
  current_retry_count: 0,
  can_be_retried: 1,
  alert_has_been_sent: 0,
  max_retry_count: MANUAL_RETRY_ATTEMPTS,
});

/**
 * Page "Suivi des retours de tournée" : suivi des envois de fin de tournée vers l'AS400 Cooperl,
 * relance (unitaire ou groupée) et correction des envois en erreur (admins), export Excel et indicateurs.
 */
export default function ApiLogsPage() {
  const config = useSelector((state) => state.config);
  const canManage = Boolean(config?.is_admin || config?.is_super_admin);

  const [selectedLog, setSelectedLog] = useState(null);
  const [isRetryModalOpen, setIsRetryModalOpen] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.FOLLOW);
  // Envois sélectionnés pour une relance groupée
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isBulkRetryModalOpen, setIsBulkRetryModalOpen] = useState(false);

  const {
    logs,
    isLoading,
    isFirstLoading,
    error,
    fetchNextPage,
    hasMore,
    nextPageError,
    retryNextPage,
    filters,
    updateFilter,
    reload: reloadLogs,
  } = useGetTourLogs();
  const { stats, reload: reloadStats } = useGetTourLogStats(filters);
  const history = useGetTourLogHistory(selectedLog?.id, canManage);
  const { retryTourLog, isLoading: isRetrying } = useRetryTourLog();
  const { updateDatas, isLoading: isSaving } = useUpdateTourLogDatas();
  const { retryTourLogs, isLoading: isBulkRetrying } = useRetryTourLogs();
  const { exportTourLogs, isLoading: isExporting } = useExportTourLogs();

  // La sélection ne survit pas à un changement de filtre (elle ne serait plus visible)
  useEffect(() => {
    setSelectedIds(new Set());
  }, [filters.query, filters.state, filters.start_date, filters.end_date]);

  // Garde le détail à jour quand la liste est rechargée
  useEffect(() => {
    if (!selectedLog || !logs?.items) {
      return;
    }
    const freshLog = logs.items.find((item) => item.id === selectedLog.id);
    if (freshLog && JSON.stringify(freshLog) !== JSON.stringify(selectedLog)) {
      setSelectedLog(freshLog);
    }
  }, [logs]);

  // Recharge l'envoi affiché, même s'il ne fait plus partie de la liste filtrée
  // (par exemple passé de « Intervention requise » à « Envoyé »)
  const refreshSelectedLog = async (log) => {
    try {
      const freshLog = await getTourLog(log);
      if (freshLog) {
        setSelectedLog((current) =>
          current?.id === freshLog.id ? freshLog : current
        );
      }
    } catch {
      // Le détail garde sa dernière version connue
    }
  };

  const refreshAll = () => {
    reloadLogs();
    reloadStats();
    history.reload();
    if (selectedLog) {
      refreshSelectedLog(selectedLog);
    }
  };

  const handleSelectLog = (log) => {
    setSelectedLog(selectedLog?.id === log.id ? null : log);
  };

  // Les mises à jour du détail ne s'appliquent qu'à l'envoi sur lequel porte l'action,
  // même si un autre envoi a été sélectionné entre-temps
  const handleConfirmRetry = async () => {
    const logId = selectedLog.id;
    const done = await retryTourLog(logId);
    setIsRetryModalOpen(false);
    if (done) {
      setSelectedLog((current) =>
        current?.id === logId ? applyRetryReset(current) : current
      );
      refreshAll();
    }
  };

  const handleSavePayload = async (datas, retry) => {
    const logId = selectedLog.id;
    const done = await updateDatas(logId, datas, retry);
    if (done) {
      setSelectedLog((current) => {
        if (current?.id !== logId) {
          return current;
        }
        const updatedLog = { ...current, datas };
        return retry ? applyRetryReset(updatedLog) : updatedLog;
      });
      refreshAll();
    }
    return done;
  };

  const handleConfirmBulkRetry = async () => {
    const result = await retryTourLogs([...selectedIds]);
    setIsBulkRetryModalOpen(false);
    if (result) {
      setSelectedIds(new Set());
      refreshAll();
    }
  };

  const onChangeDate = ([start, end]) => {
    updateFilter('start_date', start ? moment(start).format('YYYY-MM-DD') : '');
    updateFilter('end_date', end ? moment(end).format('YYYY-MM-DD') : '');
  };

  const total = logs?.pagination_info?.total_items;
  const isRefreshing = isLoading && !isFirstLoading && filters.offset === 0;

  return (
    <GenericPageContainer
      title="Suivi des retours de tournée"
      subtitle={`Données de fin de tournée transmises à l’AS400 Cooperl${
        total !== undefined
          ? ` · ${total.toLocaleString('fr-FR')} envoi${total > 1 ? 's' : ''}`
          : ''
      }`}
      filters={
        activeTab === TABS.FOLLOW && (
          <>
            <SearchFilter
              value={filters.query}
              setValue={(value) => updateFilter('query', value)}
            />
            <div className="tw-relative">
              <ReactDatePicker
                selected={
                  filters.start_date ? new Date(filters.start_date) : null
                }
                onChange={onChangeDate}
                customInput={<CustomDatePickerDashboard />}
                startDate={
                  filters.start_date ? new Date(filters.start_date) : null
                }
                endDate={filters.end_date ? new Date(filters.end_date) : null}
                selectsRange
                locale="fr"
                dateFormat="dd MMM yyyy"
                placeholderText="Date de tournée"
              />
            </div>
            <MainButton
              variant="secondary"
              // Jamais désactivé : un clic pendant un rechargement le relance (sinon il était perdu).
              // Le chargement en cours se voit sur la liste.
              icon={faRotateRight}
              text="Actualiser"
              onClick={refreshAll}
            />
            <MainButton
              variant="secondary"
              icon={faFileExcel}
              text="Exporter"
              onClick={() => exportTourLogs(filters)}
              isLoading={isExporting}
            />
          </>
        )
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4 tw-my-6">
        <div
          role="tablist"
          aria-label="Vues de la page"
          className="tw-flex tw-gap-6 tw-border-0 tw-border-b tw-border-solid tw-border-black/10"
        >
          {[
            { key: TABS.FOLLOW, label: 'Suivi' },
            { key: TABS.INDICATORS, label: 'Indicateurs' },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={activeTab === key}
              onClick={() => setActiveTab(key)}
              className={`tw-pb-2 tw-px-0 tw-bg-transparent tw-border-0 tw-border-b-2 tw-border-solid tw-text-sm tw-cursor-pointer ${
                activeTab === key
                  ? 'tw-border-main tw-font-semibold tw-text-text-dark-high-importance'
                  : 'tw-border-transparent tw-text-text-dark-medium-importance hover:tw-text-text-dark-high-importance'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {activeTab === TABS.INDICATORS && <LogsIndicators />}

        {activeTab === TABS.FOLLOW && (
          <>
            <LogsKpis
              stats={stats}
              selectedState={filters.state}
              onSelectState={(state) => updateFilter('state', state)}
            />

            {canManage && selectedIds.size > 0 && (
              <div
                role="region"
                aria-label="Sélection"
                className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-px-4 tw-py-2 tw-rounded-md tw-bg-main/10 tw-text-sm"
              >
                <span className="tw-font-medium">
                  {selectedIds.size} envoi{selectedIds.size > 1 ? 's' : ''}{' '}
                  sélectionné
                  {selectedIds.size > 1 ? 's' : ''}
                </span>
                <MainButton
                  icon={faRotateRight}
                  text="Relancer la sélection"
                  onClick={() => setIsBulkRetryModalOpen(true)}
                />
                <MainButton
                  variant="link"
                  text="Tout désélectionner"
                  onClick={() => setSelectedIds(new Set())}
                />
              </div>
            )}

            {error && (
              <p
                className="tw-m-0 tw-text-sm tw-text-status-negative"
                role="alert"
              >
                {error}
              </p>
            )}

            <div className="tw-flex tw-gap-4 tw-items-start">
              <LogsTable
                logs={logs}
                isFirstLoading={isFirstLoading}
                isRefreshing={isRefreshing}
                hasMore={hasMore}
                fetchNextPage={fetchNextPage}
                nextPageError={nextPageError}
                retryNextPage={retryNextPage}
                selectedLogId={selectedLog?.id}
                onSelectLog={handleSelectLog}
                compact={Boolean(selectedLog)}
                selectable={canManage}
                selectedIds={selectedIds}
                onSelectedIdsChange={setSelectedIds}
              />
              {selectedLog && (
                <LogDetailPanel
                  log={selectedLog}
                  canManage={canManage}
                  history={history}
                  onClose={() => setSelectedLog(null)}
                  onRetry={() => setIsRetryModalOpen(true)}
                  onEditPayload={() => setIsEditorOpen(true)}
                />
              )}
            </div>
          </>
        )}
      </div>

      {canManage && (
        <ConfirmRetryModal
          isOpen={isBulkRetryModalOpen}
          setIsOpen={setIsBulkRetryModalOpen}
          count={selectedIds.size}
          onConfirm={handleConfirmBulkRetry}
          isLoading={isBulkRetrying}
        />
      )}

      {canManage && selectedLog && (
        <>
          <ConfirmRetryModal
            isOpen={isRetryModalOpen}
            setIsOpen={setIsRetryModalOpen}
            log={selectedLog}
            onConfirm={handleConfirmRetry}
            isLoading={isRetrying}
          />
          <LogPayloadEditorModal
            isOpen={isEditorOpen}
            setIsOpen={setIsEditorOpen}
            log={selectedLog}
            onSave={handleSavePayload}
            isSaving={isSaving}
          />
        </>
      )}
    </GenericPageContainer>
  );
}
